/**
 * ChatLayout - 3-panel layout: channel tree | messages | thread/detail
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import ChannelTree from './ChannelTree';
import ChannelHeader from './ChannelHeader';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ThreadPanel from './ThreadPanel';
import SearchPanel from './SearchPanel';
import MembersSidebar from './MembersSidebar';
import PinnedMessagesPanel from './PinnedMessagesPanel';
import BookmarksPanel from './BookmarksPanel';
import ChannelSettingsModal from './ChannelSettingsModal';

type RightPanel = 'none' | 'search' | 'members' | 'pinned' | 'bookmarks';

const ChatLayout: React.FC = () => {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { currentChannel, isConnected, threadParentMessage } = useChat();
  const [rightPanel, setRightPanel] = useState<RightPanel>('none');
  const [showSettings, setShowSettings] = useState(false);

  // Reset right panel when channel changes
  useEffect(() => {
    setRightPanel('none');
  }, [currentChannel]);

  const togglePanel = (panel: RightPanel): void => {
    setRightPanel(prev => prev === panel ? 'none' : panel);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Module header with back navigation */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-2 flex items-center gap-3">
          <button
            onClick={() => navigate('/voice')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('backToHome')}
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">{t('title')}</h1>
          <div className={`text-xs px-2 py-0.5 rounded-full ${isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {isConnected ? t('connected') : t('disconnected')}
          </div>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
      {/* Left panel: Channel tree */}
      <div className="w-64 flex-shrink-0 bg-gray-900 text-white flex flex-col">
        <div className="flex-1 overflow-hidden">
          <ChannelTree />
        </div>
      </div>

      {/* Center panel: Messages */}
      <div className="flex-1 flex flex-col min-w-0">
        {currentChannel ? (
          <>
            <ChannelHeader
              onSearchClick={() => togglePanel('search')}
              onMembersClick={() => togglePanel('members')}
              onPinnedClick={() => togglePanel('pinned')}
              onBookmarksClick={() => togglePanel('bookmarks')}
              onSettingsClick={() => setShowSettings(true)}
            />
            <div className="flex-1 overflow-hidden">
              <MessageList />
            </div>
            {!currentChannel.is_archived && <MessageInput />}
            {currentChannel.is_archived && (
              <div className="px-4 py-3 bg-yellow-50 border-t text-center text-sm text-yellow-700">
                {t('archivedReadOnly')}
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="text-4xl mb-4">#</div>
              <p>{t('selectChannel')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Right panel: Thread takes priority, then other panels */}
      {threadParentMessage && (
        <div className="w-96 flex-shrink-0 border-l bg-white">
          <ThreadPanel />
        </div>
      )}
      {!threadParentMessage && rightPanel === 'search' && (
        <div className="w-96 flex-shrink-0 border-l bg-white">
          <SearchPanel onClose={() => setRightPanel('none')} />
        </div>
      )}
      {!threadParentMessage && rightPanel === 'members' && (
        <div className="w-96 flex-shrink-0 border-l bg-white">
          <MembersSidebar onClose={() => setRightPanel('none')} />
        </div>
      )}
      {!threadParentMessage && rightPanel === 'pinned' && (
        <div className="w-96 flex-shrink-0 border-l bg-white">
          <PinnedMessagesPanel onClose={() => setRightPanel('none')} />
        </div>
      )}
      {!threadParentMessage && rightPanel === 'bookmarks' && (
        <div className="w-96 flex-shrink-0 border-l bg-white">
          <BookmarksPanel onClose={() => setRightPanel('none')} />
        </div>
      )}
      </div>

      {/* Settings modal (overlay) */}
      {showSettings && currentChannel && (
        <ChannelSettingsModal
          channel={currentChannel}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default ChatLayout;
